/**
 * <SchoolAssessmentAIPreview> — AI analyze success summary (molecule, L0.1).
 *
 * Read-only green card: success header + subject / chapters / date / key-requirements rows +
 * "Modifier les infos". Consumes `UISchoolAssessmentPreview`; edit side effects stay on the parent.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { formatLongDate } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type UISchoolAssessmentPreview } from "../../../ports/primary/ui/types"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Icon } from "../../atoms/icons/Icon"
import { ImageIcon } from "../../atoms/icons/ImageIcon"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { BulletList } from "../BulletList"
import { Card } from "../Card"

export type SchoolAssessmentAIPreviewProps = {
    preview: UISchoolAssessmentPreview
    onEdit: () => void
    testID?: string
}

function PreviewRow(props: { children: React.ReactNode; row?: boolean }) {
    const { children, row = false } = props
    return (
        <Card background="surface-raised" elevation="none" p="3">
            {row ? (
                <Row gap="3" align="center">
                    {children}
                </Row>
            ) : (
                <Stack gap="1">{children}</Stack>
            )}
        </Card>
    )
}

export function SchoolAssessmentAIPreview(props: SchoolAssessmentAIPreviewProps) {
    const { preview, onEdit, testID } = props
    const chapters = preview.chapters ?? []
    const keyRequirements = preview.keyRequirements ?? []

    return (
        <Card background="successLight" p="5" style={styles.shell} testID={testID}>
            <Stack gap="4">
                <Row gap="3" align="center">
                    <View style={styles.checkBadge}>
                        <Icon name="check" size={14} color="white" />
                    </View>
                    <Text size="text-lg" weight="bold" color="text-success" style={styles.headerText}>
                        C'est bon, on a tout trouvé !
                    </Text>
                </Row>

                {preview.subject != null && (
                    <PreviewRow row>
                        {preview.subject.iconUrl != null && <ImageIcon uri={preview.subject.iconUrl} size={24} />}
                        <Stack gap="0" style={styles.flex}>
                            <Text size="text-sm" color="text-muted">
                                Matière
                            </Text>
                            <Text size="text-base" weight="semibold" color="text-primary">
                                {preview.subject.name}
                            </Text>
                        </Stack>
                    </PreviewRow>
                )}

                {chapters.length > 0 && (
                    <PreviewRow>
                        <Text size="text-sm" color="text-muted">
                            Chapitres
                        </Text>
                        <Text size="text-base" weight="semibold" color="text-primary">
                            {chapters.map((c) => c.name).join(", ")}
                        </Text>
                    </PreviewRow>
                )}

                {preview.date != null && preview.date !== "" && (
                    <PreviewRow row>
                        <Icon name="calendar-alt" size={18} color="primary" />
                        <Stack gap="0" style={styles.flex}>
                            <Text size="text-sm" color="text-muted">
                                Date du contrôle
                            </Text>
                            <Text size="text-base" weight="semibold" color="text-primary">
                                {formatLongDate(preview.date)}
                            </Text>
                        </Stack>
                    </PreviewRow>
                )}

                {keyRequirements.length > 0 && (
                    <PreviewRow>
                        <Text size="text-sm" color="text-muted">
                            À réviser
                        </Text>
                        <BulletList items={keyRequirements} gap="2" />
                    </PreviewRow>
                )}

                <TextButton size="sm" label="Modifier les infos" onPress={onEdit} />
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    shell: {
        borderWidth: 2,
        borderColor: theme.colors.success,
    },
    checkBadge: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: theme.colors.success,
        alignItems: "center" as const,
        justifyContent: "center" as const,
    },
    headerText: {
        flexShrink: 1,
    },
    flex: {
        flex: 1,
    },
}))
